import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import { Briefcase, ArrowLeft, Mail } from 'lucide-react';

const SimulationEntreprise: React.FC = () => {
    const navigate = useNavigate();

    const PRIX_BORNE = { '7.4': 1500, '11': 1800, '22': 2500, '50': 8000 };
    const PRIX_INSTALL_PAR_BORNE = 500;
    const PRIX_PLATEFORME_MOIS_BORNE = 80;
    const PLAFOND_ADVENIR_PRO = 2100;

    const [formState, setFormState] = useState(() => {
        const savedData = localStorage.getItem('entrepriseFormData');
        if (savedData) {
            return JSON.parse(savedData);
        } else {
            return {
                secteur: 'tertiaire',
                nbPlaces: 20,
                nbBornes: 4,
                puissance: 7.4,
                usage: { collaborateurs: true, flotte: false, clients: false, public: false },
                options: { plateforme: false, badge: false, connecteur: false },
            };
        }
    });

    const calculation = useMemo(() => {
        let borneUnitCost = PRIX_BORNE[formState.puissance];
        if (formState.options.connecteur) borneUnitCost *= 1.5;

        let bornesTotalCost = formState.nbBornes * borneUnitCost;
        let installTotalCost = formState.nbBornes * PRIX_INSTALL_PAR_BORNE;
        let plateformeCost = formState.options.plateforme ? formState.nbBornes * PRIX_PLATEFORME_MOIS_BORNE : 0;

        let totalBrut = bornesTotalCost + installTotalCost;

        let primeAdvenir = totalBrut * 0.30;
        let plafondPrime = formState.nbBornes * PLAFOND_ADVENIR_PRO;
        if (primeAdvenir > plafondPrime) {
            primeAdvenir = plafondPrime;
        }

        let baseFiscale = totalBrut - primeAdvenir;
        let avantageFiscal = baseFiscale * 0.15;

        let totalNet = baseFiscale - avantageFiscal;

        return { borneUnitCost, bornesTotalCost, installTotalCost, plateformeCost, totalBrut, primeAdvenir, avantageFiscal, totalNet };
    }, [formState]);

    useEffect(() => {
        localStorage.setItem('entrepriseFormData', JSON.stringify(formState));
    }, [formState]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const [category, subkey] = name.split('.');

        if (type === 'checkbox') {
            const { checked } = e.target as HTMLInputElement;
            if (category === 'usage' || category === 'options') {
                 setFormState(prev => ({ ...prev, [category]: { ...prev[category], [subkey]: checked } }));
            } else {
                 setFormState(prev => ({ ...prev, [name]: checked }));
            }
        } else {
            setFormState(prev => ({ ...prev, [name]: value }));
        }
    };

    const sendMail = () => {
        const subject = "Demande de devis via le simulateur - Entreprise";
        const body = `
Bonjour,

Voici le résumé d'une simulation effectuée sur le site ELECBORNE pour une entreprise :

ESTIMATION TOTALE : ${calculation.totalNet.toFixed(2)} €
Economie fiscale annuelle estimée : ${calculation.avantageFiscal.toFixed(2)} €

DÉTAILS DU PROJET :
---------------------------------
Secteur d'activité : ${formState.secteur}
Nombre de places de parking : ${formState.nbPlaces}
Nombre de bornes : ${formState.nbBornes}
Puissance par borne : ${formState.puissance} kW

Usages prévus : ${Object.keys(formState.usage).filter(k => formState.usage[k]).join(', ') || 'Aucun'}
Options : ${Object.keys(formState.options).filter(k => formState.options[k]).join(', ') || 'Aucune'}

Merci de bien vouloir nous contacter pour une étude approfondie.

Cordialement,
[Votre Nom / Nom de l'entreprise]
                `.trim().replace(/^ +/gm, '');

        window.location.href = `mailto:contact@elecborne.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
        <PublicLayout>
            <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
                <button onClick={() => navigate('/simulateur')} className="flex items-center text-gray-600 hover:text-primary-600 mb-6">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Retour aux simulateurs
                </button>
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                    <div className="text-center mb-8">
                        <Briefcase className="mx-auto w-12 h-12 text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900 mt-4">Simulateur IRVE - Entreprise</h1>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        {/* Formulaire */}
                        <div className="space-y-6">
                            <div>
                                <label className="font-semibold">Secteur d'activité</label>
                                <select name="secteur" value={formState.secteur} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg">
                                    <option value="tertiaire">Tertiaire</option>
                                    <option value="industrie">Industrie</option>
                                    <option value="commerce">Commerce</option>
                                    <option value="hotellerie">Hôtellerie</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-semibold">Nombre de places de parking</label>
                                <input type="number" name="nbPlaces" value={formState.nbPlaces} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg" min="1" />
                            </div>
                            <div>
                                <label className="font-semibold">Nombre de bornes</label>
                                <input type="number" name="nbBornes" value={formState.nbBornes} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg" min="1" />
                            </div>
                            <div>
                                <label className="font-semibold">Puissance par borne</label>
                                <select name="puissance" value={formState.puissance} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg">
                                    <option value="7.4">7.4kW AC</option>
                                    <option value="11">11kW AC</option>
                                    <option value="22">22kW AC</option>
                                    <option value="50">50kW DC</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-semibold">Usage des bornes</label>
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center"><input type="checkbox" name="usage.collaborateurs" checked={formState.usage.collaborateurs} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Collaborateurs</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="usage.flotte" checked={formState.usage.flotte} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Flotte de véhicules</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="usage.clients" checked={formState.usage.clients} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Clients</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="usage.public" checked={formState.usage.public} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Ouvert au public</label></div>
                                </div>
                            </div>
                            <div>
                                <label className="font-semibold">Options</label>
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center"><input type="checkbox" name="options.plateforme" checked={formState.options.plateforme} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Plateforme de gestion</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="options.badge" checked={formState.options.badge} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Badges entreprise</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="options.connecteur" checked={formState.options.connecteur} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Double connecteur</label></div>
                                </div>
                            </div>
                        </div>

                        {/* Resultat */}
                        <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500">
                            <h2 className="text-2xl font-bold text-green-800 mb-4">Votre Estimation</h2>
                            <div className="space-y-3 text-gray-700">
                                <div className="flex justify-between"><span>Bornes</span><span>{calculation.bornesTotalCost.toFixed(2)} €</span></div>
                                <div className="flex justify-between"><span>Installation</span><span>{calculation.installTotalCost.toFixed(2)} €</span></div>
                                {formState.options.plateforme && <div className="flex justify-between"><span>Plateforme</span><span>{calculation.plateformeCost.toFixed(2)} €/mois</span></div>}
                                <hr className="my-3"/>
                                <div className="flex justify-between font-bold"><span>Sous-total</span><span>{calculation.totalBrut.toFixed(2)} €</span></div>
                                <div className="flex justify-between"><span>Prime Advenir Pro</span><span className="text-green-600">- {calculation.primeAdvenir.toFixed(2)} €</span></div>
                                <div className="flex justify-between"><span>Avantage fiscal</span><span className="text-green-600">- {calculation.avantageFiscal.toFixed(2)} €</span></div>
                                <hr className="my-3"/>
                                <p className="text-center text-lg font-semibold">TOTAL NET ESTIMÉ</p>
                                <p className="text-center text-4xl font-bold text-green-700">{calculation.totalNet.toFixed(2)} €</p>
                                <p className="text-center text-sm text-gray-600 mt-2">+ {calculation.plateformeCost.toFixed(2)} €/mois si option choisie</p>
                            </div>
                            <button onClick={sendMail} className="mt-6 w-full bg-primary-600 text-white font-bold py-3 rounded-lg flex items-center justify-center hover:bg-primary-700">
                                <Mail className="w-5 h-5 mr-2" /> Envoyer mon devis
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default SimulationEntreprise;
